(ns manage.resource.content-function-version
  (:require [clojure.string :as str]
            [common.util :as util]
            [goog.string :refer [format]]
            [re-frame.core :as rf]))

(defn overview-response->versions
  "Convert an overview response map into {:name :id} map.

   Example:

   ```clojure
   (def overview-response
      #js {\"publishedContentFunction\": {:id: 10},
           \"stagedContentFunctions\": [{:name \"draft\" :id: 11}]})

   (overview-response->versions overview-response)
   ;; => {:published 10 :draft 11}
   ```"
  [overview-response]
  (let [{:keys [staged-content-functions]
         {published-id :id} :published-content-function}
        (util/m->kebab-case overview-response)]
    (reduce (fn [acc {:keys [name id]}]
              (assoc acc name id))
            {}
            (cond-> staged-content-functions
              published-id (conj {:name "published" :id published-id})))))

(def overview-success-response->versions
  "Re-frame event interceptor which converts response body of overview success
   into a map of versions and insert this as second arity.
   Subsequently removes original response.

   Example:

   ```clojure
   (rf/reg-event-fx :resource.entity/overview-success
    [content-function-version/overview-success-response->versions]
    (fn [_ [_ versions response]]
      ;;=> {:published 1 :draft 2}
      ...))
   ```"
  (rf/->interceptor
   :id :overview-success-response->versions
   :before (fn overview-success-response->versions-before
             [context]
             (let [event (get-in context [:coeffects :event])
                   response (last event)]
               (assoc-in context [:coeffects :event]
                         (-> event
                             (util/vec-insert (overview-response->versions response) 1)
                             drop-last))))))

(defn versions->names
  "Convert map {:name :id} into a set of version names."
  [versions]
  (set (keys versions)))

(defn versions->available-version
  "Get available version from given versions.
   Pass in a vector of preferable version names to find first.

   Example:

   ```clojure
   ;; pass preferable \"published\"
   (versions->available-version {\"draft\" 1 \"published\" 2} [\"published\"])
   ;; => \"published\"

   ;; default fallback to first
   (versions->available-version {\"draft\" 1 \"published\" 2})
   ;; => \"draft\"
   ```"
  ([versions] (versions->available-version versions []))
  ([versions v-find-first]
   (let [version-names (versions->names versions)
         v (->> v-find-first (remove nil?) vec)]
     (or (some version-names v)
         (first version-names)))))

;; Set map of version {:name :id}
(rf/reg-event-db :resource.content-function-version/set-versions
  [(rf/path :resource.content-function-version/versions)]
  (fn [_ [_ versions]]
    versions))

;; Activate version by given :name
(rf/reg-event-db :resource.content-function-version/activate-version
  [(rf/path :resource.content-function-version/active-version-name)]
  (fn [_ [_ version-name]]
    version-name))

;; Remove version by given name
(rf/reg-event-db :resource.content-function-version/remove-version
  [(rf/path :resource.content-function-version/version)]
  (fn [versions [_ version-name]]
    (dissoc versions version-name)))

;; Get map of versions {:name :id}
;; You can pass option boolean flag `only-staged` to filter only staged
(rf/reg-sub :resource.content-function-version/versions
  (fn [db [_ {:keys [only-staged]}]]
    (cond-> (get db :resource.content-function-version/versions {})
      only-staged (dissoc "published"))))

;; Get set of version names
(rf/reg-sub :resource.content-function-version/version-names
  :<- [:resource.content-function-version/versions]
  :<- [:resource.content-function-version/versions {:only-staged true}]
  (fn [[versions staged-versions] [_ {:keys [only-staged]}]]
    (versions->names (if only-staged staged-versions versions))))

;; Get active version {:name :id}
;; You can pass option boolean flag `only-staged` to exclude published version
(rf/reg-sub :resource.content-function-version/active-version
  (fn [db [_ {:keys [only-staged]}]]
    (when-let [active-version-name (get db :resource.content-function-version/active-version-name)]
      (when-let [version-id (cond-> (get db :resource.content-function-version/versions)
                              only-staged (dissoc "published")
                              true (get active-version-name))]
        {:name active-version-name :id version-id}))))

;; Get boolean state if given version name is present within versions
(rf/reg-sub :resource.content-function-version/contains-version?
  :<- [:resource.content-function-version/version-names]
  (fn [version-names [_ version-name]]
    (contains? version-names version-name)))

;; Get boolean state if given version name is set as active version
(rf/reg-sub :resource.content-function-version/active-version?
  :<- [:resource.content-function-version/active-version]
  (fn [active-version [_ version-name]]
    (and active-version
         (= (:name active-version) version-name))))

;; Get version options as vector with format {:label "" :value ""}
(rf/reg-sub :resource.content-function-version/version-options
  :<- [:resource.content-function-version/version-names]
  (fn [version-names _]
    (->> version-names
         (mapv (fn [version]
                 {:label (-> (format "%s content" version)
                             str/capitalize)
                  :value version})))))
